/**
 * Fase 2 C5 (Contatos) — parser vCard 3.0/4.0.
 *
 * Suporta:
 * - Múltiplos vCards no mesmo arquivo (BEGIN:VCARD/END:VCARD)
 * - Linhas continuadas (RFC 6350 §3.2: próxima linha começa com espaço/tab)
 * - Parâmetros TYPE (CELL/MOBILE → mobile; outros → phone)
 * - URI tel: (tel:+55...) — strip do prefixo
 * - Nomes brasileiros com acentos (assume UTF-8 do leitor)
 *
 * Ignora: PHOTO, BDAY, ADR, GEO, CATEGORIES, REV, PRODID, X-*, encoding
 * QUOTED-PRINTABLE não é suportado nesta versão (raro em exports modernos
 * iOS/Android/Outlook/Google).
 *
 * 06/mai/26: nomes (first_name/last_name) saem do parser já em Title Case
 * (ADR-001 PF) — VCFs costumam vir em ALL CAPS de exports antigos.
 */

import { formatPersonName } from './nameUtils';

function parseOneVCard(body) {
  const lines = body.split(/\r?\n/).filter((l) => l.trim());
  const result = {
    first_name: '',
    last_name: '',
    title: null,
    email: null,
    mobile: null,
    phone: null,
    organization: null,
    linkedin_url: null,
    raw_phones: [],
    raw_emails: [],
  };

  for (const line of lines) {
    const colonIdx = line.indexOf(':');
    if (colonIdx === -1) continue;
    const propPart = line.substring(0, colonIdx);
    const value = line.substring(colonIdx + 1).trim();
    if (!value) continue;

    const propUpper = propPart.toUpperCase();
    const params = propUpper.split(';');
    const propName = params[0];

    switch (propName) {
      case 'N': {
        const parts = value.split(';');
        result.last_name = (parts[0] || '').trim();
        result.first_name = (parts[1] || '').trim();
        break;
      }
      case 'FN': {
        if (!result.first_name && !result.last_name) {
          const parts = value.trim().split(/\s+/);
          result.first_name = parts[0] || '';
          result.last_name = parts.slice(1).join(' ');
        }
        break;
      }
      case 'ORG': {
        result.organization = value.split(';')[0].trim();
        break;
      }
      case 'TITLE': {
        result.title = value.trim();
        break;
      }
      case 'TEL': {
        const phone = value.replace(/^tel:/i, '').trim();
        result.raw_phones.push(phone);
        const typeStr = params.join(';');
        if (typeStr.includes('CELL') || typeStr.includes('MOBILE')) {
          if (!result.mobile) result.mobile = phone;
        } else if (!result.phone) {
          result.phone = phone;
        }
        break;
      }
      case 'EMAIL': {
        const email = value.trim().toLowerCase();
        result.raw_emails.push(email);
        if (!result.email) result.email = email;
        break;
      }
      case 'URL': {
        if (value.toLowerCase().includes('linkedin')) {
          result.linkedin_url = value.trim();
        }
        break;
      }
      default:
        break;
    }
  }

  // Fallback: TEL sem TYPE → primeiro=phone, segundo=mobile (cobre exports
  // antigos onde TYPE não foi preenchido).
  if (!result.phone && !result.mobile && result.raw_phones.length > 0) {
    result.phone = result.raw_phones[0];
    if (result.raw_phones.length > 1) [, result.mobile] = result.raw_phones;
  }

  // 06/mai/26: normaliza nomes em Title Case (ADR-001 PF).
  result.first_name = formatPersonName(result.first_name);
  result.last_name = formatPersonName(result.last_name);

  return result;
}

export function parseVCard(text) {
  if (!text) return [];
  // Unfold continuation lines (RFC 6350 §3.2): junta linha que começa com
  // espaço/tab à anterior.
  const unfolded = String(text).replace(/\r?\n[ \t]/g, '');
  const cards = [];
  const regex = /BEGIN:VCARD([\s\S]*?)END:VCARD/gi;
  let match = regex.exec(unfolded);
  while (match !== null) {
    cards.push(parseOneVCard(match[1]));
    match = regex.exec(unfolded);
  }
  return cards;
}
